"use client";

import { useState, useCallback, useEffect } from "react";
import { ChatBubbleOvalLeftIcon, XMarkIcon } from "@heroicons/react/24/outline";

import ServiceChat from "@/app/components/Services/Text/Chat";
import getLanguage from "@/app/utils/getlanguage";
import { useLocale } from "@/app/context/use-locale";
import translation from "./locale/index.json";

interface ChatWidgetProps {
  taskId?: string;
}

export default function ChatWidget({ taskId }: ChatWidgetProps) {
  const [isOpen, setIsOpen] = useState(false);
  const locale = useLocale();
  const content = getLanguage(translation, locale);

  // client-side fallback for user agent & language (ServiceChat uses these for context)
  const [clientInfo, setClientInfo] = useState({ userAgent: "", language: "" });

  useEffect(() => {
    setClientInfo({
      userAgent: navigator.userAgent || "",
      language: navigator.language || "",
    });
  }, []);

  const toggle = useCallback(() => setIsOpen(prev => !prev), []);

  return (
    <>
      {/* Floating icon button */}
      <button
        onClick={toggle}
        aria-label={isOpen ? content.close : content.open}
        title={isOpen ? content.close : content.open}
        className={`fixed bottom-4 right-4 z-[100] flex h-14 w-14 items-center justify-center rounded-full shadow-xl transition-all duration-300 hover:scale-105 hover:shadow-2xl ${
          isOpen
            ? "scale-0 opacity-0"
            : "brand-gradient cta-glow scale-100 opacity-100"
        }`}
      >
        <ChatBubbleOvalLeftIcon className="h-6 w-6 text-white" />
      </button>

      {/* Chat window */}
      <div
        className={`fixed bottom-20 right-4 z-[100] flex w-[calc(100vw-2rem)] max-w-[380px] flex-col overflow-hidden rounded-2xl border border-tremor-border bg-white shadow-2xl transition-all duration-300 ${
          isOpen
            ? "translate-y-0 scale-100 opacity-100"
            : "pointer-events-none translate-y-4 scale-95 opacity-0"
        }`}
        style={{ maxHeight: "min(550px, calc(100vh - 6rem))" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-tremor-border bg-white px-4 py-3">
          <h3 className="text-sm font-semibold text-tremor-content-strong">
            {content.title}
          </h3>
          <button
            onClick={toggle}
            aria-label={content.close}
            className="rounded-lg p-1 text-tremor-content-subtle transition hover:bg-tremor-background-subtle hover:text-tremor-content-strong"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Body – ServiceChat */}
        <div className="flex-1 overflow-y-auto p-3">
          <ServiceChat
            userAgent={clientInfo.userAgent}
            referer=""
            language={clientInfo.language}
            greeting=""
            minHeight="200px"
            maxHeight="280px"
            taskId={taskId}
            compact
          />
        </div>
      </div>
    </>
  );
}
